import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { SwitchCardField } from '@app/components/form/SwitchCardField';
import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TextFieldElementWithEmoji } from '@app/modules/social-export/components/inputs/TextFieldElementWithEmoji';
import { PublishFormUnsavedChangesWarning } from '@app/modules/social-export/components/PublishFormUnsavedChangesWarning';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { publishMessages } from '@app/modules/social-export/publish';
import { zodResolver } from '@hookform/resolvers/zod';
import { Grid, Stack } from '@mui/material';
import { FormContainer } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { FacebookPublishDto, FacebookSettingsDto } from '../../social-export.types';
import { FacebookPublishValues, useFacebookPublishSchema } from './useFacebookPublishSchema';

type FacebookPublishFormProps = BaseExportFormProps<FacebookPublishDto, FacebookSettingsDto>;

export function FacebookPublishForm({
  source,
  onUpload,
  defaultSettings,
  videoUrl,
  actions,
}: FacebookPublishFormProps) {
  const { $t } = useIntl();
  const schema = useFacebookPublishSchema();
  const initialValues: FacebookPublishValues = {
    title: source.title ?? '',
    description: source.description ?? '',
    thumbType: 'DEFAULT',
    thumbnailUrl: null,
    thumbOffsetMs: null,
    private: defaultSettings?.private ?? true,
    publishAt: null,
  };

  const beforeSubmit = (data: FacebookPublishValues): FacebookPublishDto => ({
    title: data.title,
    description: data.description,
    thumbnailUrl:
      data.thumbType === 'IMAGE_URL' || data.thumbType === 'VIDEO_FRAME'
        ? (data.thumbnailUrl ?? undefined)
        : undefined,
    private: data.private,
    publishAt: data.publishAt || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <PublishFormUnsavedChangesWarning />
      <ContentCluster
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <Stack spacing={3}>
          <TextFieldElementWithEmoji
            name="title"
            label={$t({
              id: 'socialExport.form.title',
              defaultMessage: 'Título',
            })}
            required
            fullWidth
          />
          <TextFieldElementWithEmoji
            name="description"
            label={$t({
              id: 'socialExport.form.description',
              defaultMessage: 'Descripción',
            })}
            required
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </ContentCluster>
      <ContentCluster
        title={$t({
          id: 'socialExport.form.thumbnailAndSettings',
          defaultMessage: 'Thumbnail y ajustes',
        })}
      >
        <Stack spacing={4}>
          <Grid container spacing={4}>
            <Grid item md={6}>
              <ThumbFieldsMui showFrameImageToField maxSizeMb={1} videoUrl={videoUrl} />
            </Grid>
          </Grid>
          <SwitchCardField
            name="private"
            label={$t({
              id: 'socialExport.form.uploadAsPrivate',
              defaultMessage: 'Cargar como video privado',
            })}
            description={$t(publishMessages.uploadAsPrivateDescription)}
          />
        </Stack>
      </ContentCluster>
      <ContentCluster
        title={$t({
          id: 'socialExport.form.schedule',
          defaultMessage: 'Programación',
        })}
      >
        <SocialPublishAtField />
      </ContentCluster>
      {actions}
    </FormContainer>
  );
}
